// Contractor Access on the guest page, Stage 5 piece 6. Words: OBSERVATIONS_SCAN_WORDS.md rev 3 section 2.
// Mockup: MOCKUP_CONTRACTOR_ACCESS.html rev 3 (approved 2026-10-12). No sign-in.
//
// Four steps on one page: Read, The Check, Who You Are, Wait. The wait updates by itself.
// Name, company and phone are remembered on this phone (decision 10, ready for a saved app profile later).
// The visit and its code stay on this phone, so closing the page and scanning again shows where it stands.
// Rules: src/contractor.ts. The server grades the check again and checks the sponsor's domain.

import "./contractor.css";
import { callServer } from "./firebase";
import type { GuestView } from "./guest-asset";
import {
  CONTRACTOR_PROGRAM_V1,
  EMPTY_DETAILS,
  MAX_JOB_DAYS,
  PRACTICE_LINE,
  checkNext,
  checkPassed,
  checkPick,
  checkPosition,
  checkStart,
  clockText,
  usShort,
  type CheckState,
  type ContractorDetails,
  type ContractorView,
} from "./contractor";

const P = CONTRACTOR_PROGRAM_V1;
const COMPANY_NAME = "Alltech";
const KEEP_DETAILS = "safetag.contractor";
const KEEP_VISIT = "safetag.cv";
const POLL_MS = 15_000;

type Step = "read" | "check" | "who" | "wait" | "practice";

export interface CaState {
  step: Step;
  check: CheckState;
  passedAt: string;
  details: ContractorDetails;
  busy: boolean;
  error: string;
  view: ContractorView | null;
  practiceSite: string;
}

const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

function head(eyebrow: string, title: string, sub = ""): string {
  return `<div class="ca-hd"><div class="ca-top"><div class="e">${esc(eyebrow)}</div><button class="ca-x" type="button" data-ca="close">Back To The Label</button></div>
    <div class="t">${esc(title)}</div>${sub ? `<div class="m">${esc(sub)}</div>` : ""}</div>`;
}

const shown = (v: string) => Boolean(v && v.trim() && v.trim() !== "—");

/** The incident energy number on the label, or null. */
export function calOf(incidentEnergy: string): number | null {
  const m = /(\d+(?:\.\d+)?)/.exec(incidentEnergy);
  return m ? Number(m[1]) : null;
}

/** Step 1: the program lines, plus this equipment when the label has data. */
export function readHtml(siteName: string, asset: GuestView | null): string {
  let eq = "";
  if (asset && !asset.isDemo && [asset.voltage, asset.incidentEnergy, asset.arcFlashBoundary, asset.limitedApproach].some(shown)) {
    const cell = (k: string, v: string) => `<div><span>${esc(k)}</span><b>${esc(shown(v) ? v : "—")}</b></div>`;
    const cal = calOf(asset.incidentEnergy);
    eq = `<div class="ca-sec">This equipment <small>· from the label you scanned</small></div><div class="ca-eq">
      ${cell("Voltage", asset.voltage)}${cell("Incident energy", asset.incidentEnergy)}${cell("Arc flash boundary", asset.arcFlashBoundary)}${cell("Limited approach", asset.limitedApproach)}</div>
      ${cal !== null && cal >= 12 ? '<div class="ca-red">12 cal/cm² or more. No energized work on this equipment. Isolate upstream.</div>' : ""}`;
  }
  return `${head("STEP 1 OF 4 · READ", "Contractor Electrical Safety", `${COMPANY_NAME} Electrical Safety Program${siteName ? ` · ${siteName}` : ""}`)}<div class="ca-body">
    <ol class="ca-ol">${P.lines.map((l) => `<li>${esc(l)}</li>`).join("")}</ol>
    ${eq}
    <button class="ca-go" type="button" data-ca="to-check">I Read It. Take The Check</button></div>`;
}

/** Step 2: one question, or the line to read again after a miss. */
export function checkHtml(s: CheckState): string {
  const q = s.queue[0] ?? 0;
  const qq = P.questions[q]!;
  if (s.missed !== null) {
    return `${head("STEP 2 OF 4 · THE CHECK", "Not quite")}<div class="ca-body">
      <p class="ca-q">${esc(qq.text)}</p>
      <button class="ca-o bad" type="button" disabled>${esc(qq.options[s.pick ?? 0])}</button>
      <div class="ca-read"><b>Read it again</b>${esc(P.lines[qq.line])}</div>
      <button class="ca-go" type="button" data-ca="next">Got It. Next</button><p class="ca-small">This question comes back at the end.</p></div>`;
  }
  const n = checkPosition(s);
  const total = P.questions.length;
  const opts = qq.options
    .map((o, i) => `<button class="ca-o${s.pick === i ? " sel" : ""}" type="button" data-caopt="${i}" aria-pressed="${s.pick === i}">${esc(o)}</button>`)
    .join("");
  return `${head("STEP 2 OF 4 · THE CHECK", `Question ${n} of ${total}`)}<div class="ca-body"><div class="ca-prog"><i style="width:${Math.round(((n - 1) / total) * 100)}%"></i></div>
    <p class="ca-q">${esc(qq.text)}</p>${opts}
    <button class="ca-go" type="button" data-ca="next"${s.pick === null ? " disabled" : ""}>Next</button>
    <p class="ca-small">All ${total} right to pass. A miss shows the line to read again, and the question comes back at the end.</p></div>`;
}

/** Step 3: who you are and who brought you in. */
export function whoHtml(d: ContractorDetails, busy: boolean, error: string, misses: number): string {
  const f = (id: string, label: string, hint: string, value: string, extra = "") =>
    `<label class="ca-f" for="${id}">${esc(label)} <small>${esc(hint)}</small><input id="${id}" value="${esc(value)}"${extra}></label>`;
  const days = Array.from({ length: MAX_JOB_DAYS }, (_, i) => i + 1)
    .map((n) => `<option value="${n}"${d.jobDays === n ? " selected" : ""}>${n} ${n === 1 ? "day" : "days"}</option>`)
    .join("");
  return `${head("STEP 3 OF 4 · WHO YOU ARE", `Passed. ${P.questions.length} of ${P.questions.length}.`, "Now tell us who brought you in")}<div class="ca-body">
    ${misses ? `<p class="ca-small">You missed ${misses} on the way and read the line again. That is on the record.</p>` : ""}
    ${f("ca-name", "Name", "remembered on this phone", d.name, ' autocomplete="name" maxlength="80"')}
    ${f("ca-company", "Company", "remembered on this phone", d.company, ' autocomplete="organization" maxlength="80"')}
    ${f("ca-phone", "Phone", "remembered on this phone", d.phone, ' type="tel" autocomplete="tel" maxlength="30"')}
    ${f("ca-super", "Your supervisor", "at your company", d.supervisor, ' maxlength="80"')}
    ${f("ca-sponsor", "Sponsor's company email", "the person who brought you in", d.sponsorEmail, ' type="email" autocomplete="off" autocapitalize="off" spellcheck="false" maxlength="120"')}
    <label class="ca-f" for="ca-days">How many days is the job?<select id="ca-days" class="ca-sel">${days}</select></label>
    ${error ? `<div class="ca-err" role="status">${esc(error)}</div>` : ""}
    <button class="ca-go" type="button" data-ca="send"${busy ? " disabled" : ""}>${busy ? "Sending…" : "Send To My Sponsor"}</button>
    <p class="ca-small">Your sponsor gets one email to confirm you. Do not start work before they confirm.</p></div>`;
}

/** Step 4: waiting, or how it ended. */
export function waitHtml(v: ContractorView, error = ""): string {
  const who = v.sponsorName || v.sponsorEmail;
  if (v.status === "Waiting") {
    return `${head("STEP 4 OF 4 · WAIT", "Waiting On Your Sponsor", v.visitId)}<div class="ca-body"><div class="ca-wait"><b>Waiting on ${esc(v.sponsorEmail)} to confirm.</b><span>This page updates by itself.</span><span class="exp">Expires ${esc(clockText(v.expiresAt))}</span></div>
      <p class="ca-small">Your sponsor can call you at ${esc(v.phone)}. Do not start work before they confirm.</p>
      ${error ? `<div class="ca-err" role="status">${esc(error)}</div>` : ""}
      <button class="ca-plain" type="button" data-ca="refresh">Check Now</button></div>`;
  }
  if (v.status === "On Site" || v.status === "Done") {
    const sub = [v.siteName, `${v.jobDays}-day job`, v.endsOn ? `ends ${usShort(v.endsOn)}` : ""].filter(Boolean).join(" · ");
    return `${head(`CONFIRMED · ${v.visitId}`, v.status === "Done" ? "This Job Is Done" : "You Are Confirmed", sub)}<div class="ca-body">
      <p class="ca-big">Confirmed by ${esc(who)}. ${esc(who)} is responsible for you on this site. Check in before you start.</p>
      <ul class="ca-ul"><li>Lockouts use the ${COMPANY_NAME} LOTO Permit.</li><li>Live work needs an approved EEWP.</li><li>Any ${COMPANY_NAME} QEW can stop your work.</li><li>Report any injury or near miss to ${esc(who)} right away.</li></ul>
      <p class="ca-small">Reference ${esc(v.visitId)}.</p>
      <button class="ca-go" type="button" data-ca="close">Back To The Label</button>
      ${v.status === "Done" ? '<button class="ca-plain" type="button" data-ca="restart">Start A New Visit</button>' : ""}</div>`;
  }
  const body =
    v.status === "Not Mine"
      ? `<p class="ca-big red">${esc(who)} did not confirm you. Do not start.</p><p>Find the person who brought you in.</p>`
      : `<p class="ca-big red">No answer in 4 hours. Do not start.</p><p>Call the person who brought you in.</p>`;
  return `${head(v.visitId, "Not Confirmed")}<div class="ca-body">${body}
    <button class="ca-plain" type="button" data-ca="restart">Start Again</button></div>`;
}

export function practiceHtml(siteName: string): string {
  return `${head("STEP 4 OF 4 · PRACTICE", "Practice Run Done", siteName)}<div class="ca-body">
    <p class="ca-big">${esc(PRACTICE_LINE)}</p>
    <p>This is a demo label. On a real label your sponsor gets an email here, and you wait on this page until they confirm you.</p>
    <button class="ca-go" type="button" data-ca="close">Back To The Label</button></div>`;
}

export function contractorHtml(s: CaState, siteName: string, asset: GuestView | null): string {
  let inner: string;
  if (s.step === "read") inner = readHtml(siteName, asset);
  else if (s.step === "check") inner = checkHtml(s.check);
  else if (s.step === "who") inner = whoHtml(s.details, s.busy, s.error, s.check.misses);
  else if (s.step === "practice") inner = practiceHtml(s.practiceSite || siteName);
  else if (s.view) inner = waitHtml(s.view, s.error);
  else inner = `${head("CONTRACTOR ACCESS", "Opening your visit")}<div class="ca-body">${s.error ? `<div class="ca-err">${esc(s.error)}</div><button class="ca-plain" type="button" data-ca="restart">Start Again</button>` : "<p>One moment…</p>"}</div>`;
  return `<div class="ca ca-page">${inner}</div>`;
}

// ---- Storage on this phone. Every read and write can fail; the page works without it. ----

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
function writeJson(key: string, v: unknown): void {
  try {
    if (v === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* private mode: nothing kept */
  }
}

interface KeptVisit {
  visitId: string;
  ck: string;
  company: string;
}

export interface ContractorController {
  open(): void;
  close(): void;
  isOpen(): boolean;
  html(siteName: string, asset: GuestView | null): string;
  /** True when the click was the contractor page's. */
  click(ev: Event): boolean;
  input(ev: Event): boolean;
}

/** One contractor flow on the guest page. draw() redraws the whole page; onClose goes back to the label. */
export function contractorController(assetId: string, company: string, deviceId: () => string, draw: () => void, onClose: () => void): ContractorController {
  let s: CaState | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;

  const stopPoll = () => {
    if (timer) clearInterval(timer);
    timer = null;
  };

  function fresh(): CaState {
    const kept = readJson<Partial<ContractorDetails>>(KEEP_DETAILS) ?? {};
    return {
      step: "read",
      check: checkStart(),
      passedAt: "",
      details: { ...EMPTY_DETAILS, name: kept.name ?? "", company: kept.company ?? "", phone: kept.phone ?? "" },
      busy: false,
      error: "",
      view: null,
      practiceSite: "",
    };
  }

  async function poll(): Promise<void> {
    const kept = readJson<KeptVisit>(KEEP_VISIT);
    if (!s || !kept) return;
    try {
      const view = await callServer<ContractorView>("loadContractorVisit", { company: kept.company, visitId: kept.visitId, ck: kept.ck });
      if (!s) return;
      s = { ...s, view, error: "" };
      if (view.status !== "Waiting") stopPoll();
    } catch (e) {
      if (!s) return;
      const msg = e instanceof Error ? e.message : String(e);
      // A visit this phone no longer holds starts over.
      if (!s.view) {
        writeJson(KEEP_VISIT, null);
        s = fresh();
      } else s = { ...s, error: msg };
    }
    draw();
  }

  function startPoll(): void {
    stopPoll();
    timer = setInterval(() => {
      if (document.visibilityState === "visible") void poll();
    }, POLL_MS);
  }

  async function send(): Promise<void> {
    if (!s || s.busy) return;
    const d = s.details;
    writeJson(KEEP_DETAILS, { name: d.name.trim(), company: d.company.trim(), phone: d.phone.trim() });
    s = { ...s, busy: true, error: "" };
    draw();
    try {
      const res = await callServer<{ practice: true; siteName: string } | { practice: false; visitId: string; ck: string; view: ContractorView }>("startContractorVisit", {
        company,
        assetId,
        answers: s.check.answers,
        misses: s.check.misses,
        passedAt: s.passedAt,
        details: d,
        deviceId: deviceId(),
      });
      if (!s) return;
      if (res.practice) s = { ...s, busy: false, step: "practice", practiceSite: res.siteName };
      else {
        writeJson(KEEP_VISIT, { visitId: res.visitId, ck: res.ck, company } satisfies KeptVisit);
        s = { ...s, busy: false, step: "wait", view: res.view };
        startPoll();
      }
    } catch (e) {
      if (s) s = { ...s, busy: false, error: e instanceof Error ? e.message : String(e) };
    }
    draw();
    window.scrollTo(0, 0);
  }

  return {
    open() {
      const kept = readJson<KeptVisit>(KEEP_VISIT);
      s = fresh();
      if (kept && kept.company === company) {
        // A visit already on this phone: show where it stands.
        s = { ...s, step: "wait" };
        void poll().then(() => {
          if (s?.view?.status === "Waiting") startPoll();
        });
      }
      window.scrollTo(0, 0);
    },
    close() {
      stopPoll();
      s = null;
      onClose();
    },
    isOpen: () => s !== null,
    html: (siteName, asset) => (s ? contractorHtml(s, siteName, asset) : ""),
    click(ev) {
      if (!s) return false;
      const t = ev.target as HTMLElement;
      const opt = t.closest<HTMLElement>("[data-caopt]");
      if (opt) {
        s = { ...s, check: checkPick(s.check, Number(opt.dataset.caopt)) };
        draw();
        return true;
      }
      const el = t.closest<HTMLElement>("[data-ca]");
      if (!el) return false;
      const a = el.dataset.ca;
      if (a === "close") {
        this.close();
        return true;
      }
      if (a === "to-check") s = { ...s, step: "check", check: checkStart() };
      else if (a === "next") {
        const check = checkNext(s.check);
        s = checkPassed(check) ? { ...s, check, step: "who", passedAt: new Date().toISOString() } : { ...s, check };
      } else if (a === "send") {
        void send();
        return true;
      } else if (a === "refresh") {
        void poll();
        return true;
      } else if (a === "restart") {
        stopPoll();
        writeJson(KEEP_VISIT, null);
        s = fresh();
      } else return false;
      draw();
      window.scrollTo(0, 0);
      return true;
    },
    input(ev) {
      if (!s) return false;
      const t = ev.target as HTMLInputElement | HTMLSelectElement;
      const map: Record<string, keyof ContractorDetails> = { "ca-name": "name", "ca-company": "company", "ca-phone": "phone", "ca-super": "supervisor", "ca-sponsor": "sponsorEmail", "ca-days": "jobDays" };
      const k = map[t.id];
      if (!k) return false;
      s = { ...s, details: { ...s.details, [k]: k === "jobDays" ? Number(t.value) : t.value } };
      return true;
    },
  };
}
